import clsx from "clsx";
import { useRef, useState } from "react";

import StarIcon from "@/icons/star.svg";

type RatingProps = {
  size?: "sm" | "md";
  disabled?: boolean;
  maxRating?: number;
  precision?: number;
  value?: number;
  onClick: (rating: number) => void;
};

const sizeClassName = {
  sm: "w-3 h-3",
  md: "w-5 h-5",
};

export const Rating = ({
  size = "md",
  disabled,
  maxRating = 5,
  precision = 0.5,
  value = -1,
  onClick,
}: RatingProps) => {
  const [hoverActiveStar, setHoverActiveStar] = useState(value);
  const [isHovered, setIsHovered] = useState(false);
  const ratingContainerRef = useRef(null);

  const calculateRating = (e) => {
    const { width, left } = ratingContainerRef.current.getBoundingClientRect();
    let percent = (e.clientX - left) / width;
    const numberInStars = percent * maxRating;
    const nearestNumber =
      Math.round((numberInStars + precision / 2) / precision) * precision;

    return Number(
      nearestNumber.toFixed(precision.toString().split(".")[1]?.length || 0),
    );
  };

  const handleClick = (e) => {
    setIsHovered(false);
    onClick(calculateRating(e));
  };

  const handleMouseMove = (e) => {
    setIsHovered(true);
    setHoverActiveStar(calculateRating(e));
  };

  const handleMouseLeave = (e) => {
    setHoverActiveStar(-1); // Reset to default state
    setIsHovered(false);
  };

  return (
    <button
      type="button"
      disabled={disabled}
      className="relative inline-flex space-x-2 text-left"
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      ref={ratingContainerRef}
    >
      {[...new Array(maxRating)].map((_, index) => {
        const activeState = isHovered ? hoverActiveStar : value;

        const showEmptyIcon = activeState === -1 || activeState < index + 1;

        const isActiveRating = activeState !== 1;
        const isRatingWithPrecision = activeState % 1 !== 0;
        const isRatingEqualToIndex = Math.ceil(activeState) === index + 1;
        const showRatingWithPrecision =
          isActiveRating && isRatingWithPrecision && isRatingEqualToIndex;

        return (
          <div className="relative" key={index}>
            <div
              style={{
                width: showRatingWithPrecision
                  ? `${(activeState % 1) * 100}%`
                  : "0%",
              }}
              className="absolute overflow-hidden"
            >
              <StarIcon className={clsx(`text-star`, sizeClassName[size])} />
            </div>
            <div>
              {showEmptyIcon ? (
                <StarIcon
                  className={clsx("text-gray-300", sizeClassName[size])}
                />
              ) : (
                <StarIcon className={clsx(`text-star`, sizeClassName[size])} />
              )}
            </div>
          </div>
        );
      })}
    </button>
  );
};
